import React from "react";
// ANT DESIGN COMPONENTS
import { Button, Select, Checkbox, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faDownload,
  faRefresh,
  faRotateRight,
  faStop,
  faTable,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  updateTerminalResetTable,
  updateTerminalDownloadReport,
  updateTerminalAutoScroll,
  updateShowTerminalJsonViewer,
  updateShowTerminalTable,
  updateTerminalShowCarHallCalls,
  updateTerminalShowBankFloors,
  updateTerminalIconMode,
  updateTerminalMode,
  updateTerminalShowTxMessages,
  updateShowTerminalCommandPanel,
} from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import { useClearBankMessages } from "../socketMessageState/BankMessagesContext";
// INTERFACES
import { RootState } from "../../../app/store";
import { TerminalGlobalObject, TerminalFilterType } from "../../../interfaces/Monitoring.interface";
import { formatUuidT } from "../../../flatbuffers/display";

function TerminalFilters({
  terminalBanks,
  terminalLifts,
  setBankId,
  bankId,
  setLiftId,
  setChannelId,
  setServerId,
  setBankLiftId,
  liftId,
  filterType,
  setFilterType,
  onClickDownloadJsonFile,
  onClickStartCapture,
  onClickStopCapture,
  onClickDownloadCapture,
  onClickRefreshConnection,
}: {
  terminalBanks: TerminalGlobalObject[];
  terminalLifts: TerminalGlobalObject[];
  setBankId: (newValue: string) => void;
  bankId: string;
  setLiftId: (newValue: string) => void;
  setChannelId: (newValue: string) => void;
  setServerId: (newValue: string) => void;
  setBankLiftId: (newValue: string) => void;
  liftId: string;
  filterType: TerminalFilterType;
  setFilterType: (newValue: TerminalFilterType) => void;
  onClickDownloadJsonFile: () => void;
  onClickStartCapture: () => void;
  onClickStopCapture: () => void;
  onClickDownloadCapture: () => void;
  onClickRefreshConnection: () => void;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const { Option } = Select;
  const dispatch = useDispatch();
  const clearMessages = useClearBankMessages();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    terminalAutoScroll,
    showTerminalJsonViewer,
    showTerminalTable,
    terminalShowCarHallCalls,
    terminalShowBankFloors,
    terminalIconMode,
    terminalMode,
    binaryCaptureActive,
    binaryCaptureReady,
    terminalShowTxMessages,
    showTerminalCommandPanel,
  } = useSelector((state: RootState) => state.monitoring);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickResetTable = () => {
    dispatch(updateTerminalResetTable(true));
    setTimeout(() => {
      dispatch(updateTerminalResetTable(false));
    }, 100);
  };

  const onClickDownloadExcel = () => {
    dispatch(updateTerminalDownloadReport(true));
    setTimeout(() => {
      dispatch(updateTerminalDownloadReport(false));
    }, 100);
  };

  const onChangeAutoScroll = (e: any) => {
    dispatch(updateTerminalAutoScroll(e.target.checked));
  };

  const onChangeShowTable = (e: any) => {
    dispatch(updateShowTerminalTable(e.target.checked));
  };

  const onChangeShowJsonViewer = (e: any) => {
    dispatch(updateShowTerminalJsonViewer(e.target.checked));
  };

  const onChangeShowCarHallCalls = (e: any) => {
    dispatch(updateTerminalShowCarHallCalls(e.target.checked));
  };

  const onChangeShowBankFloors = (e: any) => {
    dispatch(updateTerminalShowBankFloors(e.target.checked));
  };

  const onChangeShowTxMessages = (e: any) => {
    dispatch(updateTerminalShowTxMessages(e.target.checked));
  };

  const onChangeShowCommandPanel = (e: any) => {
    dispatch(updateShowTerminalCommandPanel(e.target.checked));
  };

  const onChangeIconMode = (e: any) => {
    dispatch(updateTerminalIconMode(e.target.checked ? "text" : "svg"));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__flex">
      {/** MODE TOGGLE */}
      <div className="generalStyles__mrFix" style={{ width: "130px" }}>
        <Select
          className="generalStyles__width100 generalStyles__textCenter"
          value={terminalMode}
          onChange={(value: "websocket" | "endpoint") => {
            dispatch(updateTerminalMode(value));
          }}
        >
          <Option value="websocket">WebSocket</Option>
          <Option value="endpoint">Endpoint</Option>
        </Select>
      </div>
      {/** LIFT */}
      {terminalMode === "websocket" && (
        <>
          <div className="generalStyles__mrFix" style={{ width: "130px" }}>
            <Select
              className="generalStyles__width100 generalStyles__textCenter "
              showSearch
              optionFilterProp="children"
              allowClear={false}
              value={filterType}
              placeholder={t("general.noLiftSelected")}
              onChange={(e: TerminalFilterType) => {
                setFilterType(e);
                setBankId("");
                setLiftId("");
                setServerId("");
                setChannelId("");
                setBankLiftId("");
                onClickResetTable();
              }}
            >
              {["ALL", "UNIT", "BANK"].map((item: string) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </Select>
          </div>
          <div className="generalStyles__mrFix" style={{ width: "250px" }}>
            {filterType === "BANK" ? (
              <Select
                className="generalStyles__width100 generalStyles__textCenter "
                showSearch
                optionFilterProp="children"
                allowClear={false}
                value={bankId}
                placeholder={t("general.noLiftSelected")}
                onChange={(e: string) => {
                  setBankId(e);
                  setLiftId("");
                  setBankLiftId("");
                  onClickResetTable();
                  const index = terminalBanks.findIndex(
                    (item: TerminalGlobalObject) => item.id === e,
                  );
                  if (index >= 0) {
                    setChannelId(terminalBanks[index].channelId);
                    setServerId(terminalBanks[index].serverId);
                  }
                }}
              >
                {terminalBanks.map((item: { id: string; name: string }) => (
                  <Option key={formatUuidT(item.id)} value={formatUuidT(item.id)}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            ) : (
              <Select
                className="generalStyles__width100 generalStyles__textCenter "
                showSearch
                optionFilterProp="children"
                allowClear={false}
                value={liftId}
                placeholder={t("general.noLiftSelected")}
                disabled={filterType === "ALL"}
                onChange={(e: string) => {
                  setLiftId(e);
                  setBankId("");
                  const index = terminalLifts.findIndex(
                    (item: TerminalGlobalObject) => item.id === e,
                  );
                  if (index >= 0) {
                    setChannelId(terminalLifts[index].channelId);
                    setServerId(terminalLifts[index].serverId);
                    setBankLiftId(terminalLifts[index].bankId);
                  }
                  onClickResetTable();
                }}
              >
                {terminalLifts.map((item: { id: string; name: string }) => (
                  <Option key={formatUuidT(item.id)} value={formatUuidT(item.id)}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            )}
          </div>
        </>
      )}
      {terminalMode === "websocket" && (
        <>
          <div>
            <div>
              <Tooltip title={t("general.downloadExcelFile")}>
                <Button
                  className="buttonStyle__13 generalStyles__mrFix "
                  onClick={onClickDownloadExcel}
                  icon={<FontAwesomeIcon icon={faDownload} />}
                />
              </Tooltip>
            </div>
          </div>
          <div>
            <div>
              <Tooltip title={t("general.downloadJsonFile")}>
                <Button
                  className="buttonStyle__3 generalStyles__mrFix "
                  onClick={onClickDownloadJsonFile}
                  icon={<FontAwesomeIcon icon={faDownload} />}
                />
              </Tooltip>
            </div>
          </div>
          {!binaryCaptureActive && (
            <div>
              <Tooltip title={t("general.startCapture")}>
                <Button
                  className="buttonStyle__17 generalStyles__mrFix"
                  onClick={onClickStartCapture}
                  icon={<FontAwesomeIcon icon={faCircle} />}
                />
              </Tooltip>
            </div>
          )}
          {binaryCaptureActive && (
            <div>
              <Tooltip title={t("general.stopCapture")}>
                <Button
                  className="buttonStyle__4 generalStyles__mrFix"
                  onClick={onClickStopCapture}
                  icon={<FontAwesomeIcon icon={faStop} />}
                />
              </Tooltip>
            </div>
          )}
          {binaryCaptureReady && !binaryCaptureActive && (
            <div>
              <Tooltip title={t("general.downloadBinaryFile")}>
                <Button
                  className="buttonStyle__3 generalStyles__mrFix"
                  onClick={onClickDownloadCapture}
                  icon={<FontAwesomeIcon icon={faDownload} />}
                />
              </Tooltip>
            </div>
          )}
          <div>
            <div>
              <Button
                className="buttonStyle__1  generalStyles__mrFix "
                onClick={onClickResetTable}
                icon={<FontAwesomeIcon className="generalStyles__mrFix " icon={faTable} />}
              >
                Reset Table
              </Button>
            </div>
          </div>
          <div>
            <div>
              <Button
                className="buttonStyle__7  generalStyles__mrFix "
                onClick={clearMessages}
                icon={<FontAwesomeIcon className="generalStyles__mrFix " icon={faRefresh} />}
              >
                Reset State
              </Button>
            </div>
          </div>
          <div>
            <div>
              <Tooltip title={t("general.refreshWebSocket")}>
                <Button
                  className="buttonStyle__2  generalStyles__mrFix "
                  onClick={onClickRefreshConnection}
                  icon={<FontAwesomeIcon className="generalStyles__mrFix " icon={faRotateRight} />}
                >
                  Refresh WS
                </Button>
              </Tooltip>
            </div>
          </div>
          <div style={{ marginTop: "5px" }}>
            <Checkbox onChange={onChangeShowTable} checked={showTerminalTable}>
              Show table
            </Checkbox>
          </div>
          <div style={{ marginTop: "5px" }}>
            <Checkbox onChange={onChangeShowJsonViewer} checked={showTerminalJsonViewer}>
              Show JSON viewer
            </Checkbox>
          </div>
          <div style={{ marginTop: "5px" }}>
            <Checkbox onChange={onChangeAutoScroll} checked={terminalAutoScroll}>
              Auto scroll
            </Checkbox>
          </div>
          <div style={{ marginTop: "5px" }}>
            <Checkbox onChange={onChangeShowCarHallCalls} checked={terminalShowCarHallCalls}>
              Show car calls and hall calls
            </Checkbox>
          </div>

          <div style={{ marginTop: "5px" }}>
            <Checkbox onChange={onChangeShowBankFloors} checked={terminalShowBankFloors}>
              Bank Floors
            </Checkbox>
          </div>
          <div style={{ marginTop: "5px" }}>
            <Checkbox onChange={onChangeIconMode} checked={terminalIconMode === "text"}>
              Show icon as text
            </Checkbox>
          </div>
          <div style={{ marginTop: "5px" }}>
            <Checkbox onChange={onChangeShowCommandPanel} checked={showTerminalCommandPanel}>
              Show command panel
            </Checkbox>
          </div>
          <div style={{ marginTop: "5px" }}>
            <Checkbox onChange={onChangeShowTxMessages} checked={terminalShowTxMessages}>
              Show sent commands (TX)
            </Checkbox>
          </div>
        </>
      )}
      {terminalMode === "endpoint" && (
        <div style={{ marginTop: "5px" }}>
          <Checkbox onChange={onChangeAutoScroll} checked={terminalAutoScroll}>
            Auto scroll
          </Checkbox>
        </div>
      )}
    </div>
  );
}

export default TerminalFilters;
